import React, { useState, useEffect } from "react";
import { EsBadge } from "./EsBadge";
import { CorrettoBadge } from "./CorrettoBadge";
import { ElencoCorrezioniBtn, useCorrezioni } from "./ElencoCorrezioni";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X, Search, AlertTriangle, ChevronDown, Cross } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { ExportButton } from "./ExportButton";
import { useSegnalazioni, SegnalazioneButton, SegnalazioniBadge } from "./Segnalazioni";
import { ConfirmUndoDialog } from "./ConfirmUndoDialog";
import { MeseGiorniEsplosi, RecapOperatoriMese, RecapGiorno, AllertaBox } from "./MeseGiorniEsplosi";
import { UscitiInfoBox, isDimesso } from "./UscitiInfoBox";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const fmtLong = (iso) => { try { const d = new Date(iso); const s = d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }); return s.charAt(0).toUpperCase() + s.slice(1); } catch { return iso; } };
const fmtRel = (iso) => {
  if (!iso) return "";
  try {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const d = new Date(iso + "T00:00:00");
    const diff = Math.round((t - d) / 86400000);
    if (diff === 0) return "oggi";
    if (diff === 1) return "ieri";
    return d.toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "long" });
  } catch { return iso; }
};

function FleboDialog({ ctx, options, label, onClose, onSave }) {
  const [tipo, setTipo] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { if (ctx) { setTipo(ctx.rec?.flebo || ""); setNote(ctx.rec?.flebo_note || ""); } }, [ctx]);
  const pill = (active) => `px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${active ? "text-white border-transparent" : "bg-secondary hover:bg-secondary/70 border-transparent text-foreground"}`;
  return (
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="alim-flebo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Cross size={18} className="text-emerald-600" /> Aiuto sanitario</DialogTitle>
          <DialogDescription>{ctx?.nome} — {label}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold mb-1.5 block">Tipo</label>
            <div className="flex flex-wrap gap-2" data-testid="alim-flebo-tipi">
              {options.map((a) => (
                <button key={a.v} onClick={() => setTipo(tipo === a.v ? "" : a.v)} className={pill(tipo === a.v)} style={tipo === a.v ? { background: "#059669" } : {}} data-testid={`alim-flebo-tipo-${a.v}`}>{a.l}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block">Note (facoltative)</label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Dettagli (es. quantità, orario, reazione)..." data-testid="alim-flebo-note" />
          </div>
        </div>
        <DialogFooter>
          {ctx?.rec?.flebo && <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 mr-auto" onClick={() => onSave({ tipo: "", note: "" })} data-testid="alim-flebo-rimuovi">Rimuovi</Button>}
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => { if (!tipo) { toast.error("Seleziona il tipo"); return; } onSave({ tipo, note: note || "" }); }} disabled={!tipo} className="bg-emerald-600 hover:bg-emerald-700" data-testid="alim-flebo-salva"><Check size={15} className="mr-1" /> Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AlimentazioneControllo({ tipo, title, subtitle, Icon, momentiOptions, verbo, momentiLabel, testid, flebiOptions = [], flebiLabel = "" }) {
  const { selectedStructure, can } = useAuth();
  const { activeOperatore, opName, canSign, signerName, mustUnlock } = useFirma();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmUndo, setConfirmUndo] = useState(null);
  const today = ymd(new Date());
  const [openDay, setOpenDay] = useState(today);
  const [mese, setMese] = useState(today.slice(0, 7));
  const selDate = openDay || today;
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState("giorno");
  const monthStart = today.slice(0, 8) + "01";
  const [stoDa, setStoDa] = useState(monthStart);
  const [stoA, setStoA] = useState(today);
  const [expanded, setExpanded] = useState({});
  const [showDimessi, setShowDimessi] = useState(false);
  const [noteDraft, setNoteDraft] = useState({});
  const [flebo, setFlebo] = useState(null);

  const { data: meseData } = useQuery({
    queryKey: ["alim-mese", tipo, selectedStructure, mese],
    queryFn: async () => (await api.get("/alimentazione/mese", { params: { tipo, structure_id: selectedStructure, mese } })).data,
  });
  const { data: alimAllerte = [] } = useQuery({
    queryKey: ["alim-allerte", tipo, selectedStructure],
    queryFn: async () => (await api.get("/alimentazione/allerte", { params: { tipo, structure_id: selectedStructure, giorni: 2 } })).data,
  });
  const { data: storico = [] } = useQuery({
    enabled: mode === "storico",
    queryKey: ["alim-storico", tipo, selectedStructure, stoDa, stoA],
    queryFn: async () => (await api.get("/alimentazione/storico", { params: { tipo, structure_id: selectedStructure, da: stoDa, a: stoA } })).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["alim-grid", tipo, selectedStructure, selDate],
    queryFn: async () => (await api.get("/alimentazione/grid", { params: { tipo, structure_id: selectedStructure, data: selDate } })).data,
  });

  const rowsAll = data?.rows || [];
  const records = data?.records || {};
  const categoria = `non_${tipo}`;
  const { byKey: segByKey, aperte: segAperte, refetch: segRefetch } = useSegnalazioni(categoria, selectedStructure, selDate);
  const corrMap = useCorrezioni(tipo, selectedStructure, selDate.slice(0, 7));

  const save = async (r, stato, momenti, note) => {
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per registrare"); return; }
    const rec = records[r.ospite_id] || {};
    const payload = {
      tipo, ospite_id: r.ospite_id, structure_id: r.structure_id, data: selDate, stato, operatore: signerName,
      momenti: stato === "segnalato" ? (momenti ?? rec.momenti ?? []) : [],
      note: stato === "segnalato" ? (note ?? rec.note ?? "") : "",
    };
    try {
      await api.post("/alimentazione/register", payload);
      qc.invalidateQueries({ queryKey: ["alim-grid", tipo] });
      qc.invalidateQueries({ queryKey: ["alim-mese", tipo] });
      qc.invalidateQueries({ queryKey: ["correzioni"] });
      if (stato === "segnalato" && momenti === undefined) toast.warning(`Segnalato: non ha ${verbo}`);
    } catch { toast.error("Errore nel salvataggio"); }
  };
  const toggleMomento = (r, m) => {
    const rec = records[r.ospite_id] || {};
    const cur = rec.momenti || [];
    const next = cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m];
    save(r, "segnalato", next, rec.note);
  };
  const onOk = (r) => {
    const rec = records[r.ospite_id] || {};
    // Predefinito: l'ospite ha sempre mangiato/bevuto -> il tasto verde non è cliccabile.
    // È attivo solo per annullare una segnalazione ("rispuntare").
    if (rec.stato !== "segnalato") return;
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per registrare"); return; }
    setConfirmUndo({ nome: `${r.cognome} ${r.nome}`, extra: `ha ${verbo}`, action: () => { save(r, ""); setExpanded((p) => ({ ...p, [r.ospite_id]: false })); } });
  };
  const onNo = (r) => {
    const rec = records[r.ospite_id] || {};
    if (rec.stato === "segnalato") { setExpanded((p) => ({ ...p, [r.ospite_id]: true })); setNoteDraft((p) => ({ ...p, [r.ospite_id]: rec.note || "" })); return; }
    save(r, "segnalato");
    setExpanded((p) => ({ ...p, [r.ospite_id]: true }));
    setNoteDraft((p) => ({ ...p, [r.ospite_id]: "" }));
  };
  const onSalva = (r) => {
    const rec = records[r.ospite_id] || {};
    const note = noteDraft[r.ospite_id] ?? rec.note ?? "";
    save(r, "segnalato", rec.momenti || [], note);
    setExpanded((p) => ({ ...p, [r.ospite_id]: false }));
    toast.success("Segnalazione salvata");
  };

  const openFlebo = (r) => {
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per registrare"); return; }
    setFlebo({ r, nome: `${r.cognome} ${r.nome}`, rec: records[r.ospite_id] || {} });
  };
  const saveFlebo = async (vals) => {
    const r = flebo.r;
    const rec = records[r.ospite_id] || {};
    const payload = {
      tipo, ospite_id: r.ospite_id, structure_id: r.structure_id, data: selDate, stato: rec.stato || "",
      operatore: signerName, momenti: rec.momenti || [], note: rec.note || "",
      flebo: vals.tipo, flebo_note: vals.note,
    };
    try {
      await api.post("/alimentazione/register", payload);
      qc.invalidateQueries({ queryKey: ["alim-grid", tipo] });
      qc.invalidateQueries({ queryKey: ["correzioni"] });
      setFlebo(null);
      toast.success(vals.tipo ? "Flebo registrata" : "Flebo rimossa");
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const q = search.trim().toLowerCase();
  const isFuori = (r) => ["ospedale", "uscita"].includes(r.presenza);
  const fuoriLabel = (r) => (r.presenza === "ospedale" ? "Ricoverato" : "Uscito");
  const rows = rowsAll.filter((r) => !q || `${r.cognome} ${r.nome}`.toLowerCase().includes(q));
  const isDimessoR = isDimesso;
  const rowsPresenti = rowsAll.filter((r) => !(r.fuori_giorno ?? isFuori(r)) && !isDimessoR(r));
  const segnCount = rowsPresenti.filter((r) => records[r.ospite_id]?.stato === "segnalato").length;
  const alimOpCounts = {};
  rowsPresenti.forEach((r) => { const rec = records[r.ospite_id]; if (rec?.stato) { const n = rec.operatore || "—"; alimOpCounts[n] = (alimOpCounts[n] || 0) + 1; } });
  const alimOperatoriGiorno = Object.entries(alimOpCounts).map(([nome, count]) => ({ nome, count })).sort((a, b) => b.count - a.count);
  const alimRegistrate = rowsPresenti.filter((r) => records[r.ospite_id]?.stato).length;
  const alimStats = [
    { key: "segnalati", label: "Segnalati", value: segnCount, tone: segnCount > 0 ? "bad" : "ok" },
    { key: "controlli", label: "Controlli firmati", value: alimRegistrate, tone: "muted" },
    { key: "ospiti", label: "Ospiti presenti", value: rowsPresenti.length, tone: "muted" },
  ];

  const exportRows = rowsAll.map((r) => { const rec = records[r.ospite_id] || {}; return { ...r, _stato: rec.stato === "segnalato" ? `NON ha ${verbo}` : "OK", _mom: (rec.momenti || []).join(", "), _note: rec.note || "", _flebo: rec.flebo ? (flebiOptions.find((f) => f.v === rec.flebo)?.l || rec.flebo) : "" }; });
  const cols = [
    { header: "Ospite", accessor: (r) => `${r.cognome} ${r.nome}` },
    { header: "Camera/zona", accessor: (r) => r.camera || "" },
    { header: "Esito", accessor: (r) => r._stato },
    { header: momentiLabel, accessor: (r) => r._mom },
    { header: "Aiuto sanitario", accessor: (r) => r._flebo },
    { header: "Nota", accessor: (r) => r._note },
  ];
  const stoCols = [
    { header: "Data", accessor: (r) => r.data },
    { header: "Ospite", accessor: (r) => r.ospite_nome },
    { header: "Camera/zona", accessor: (r) => r.camera || "" },
    { header: momentiLabel, accessor: (r) => (r.momenti || []).join(", ") },
    { header: "Nota", accessor: (r) => r.note || "" },
    { header: "Registrato da", accessor: (r) => r.updated_by || "" },
  ];
  const qs = search.trim().toLowerCase();
  const storicoF = storico.filter((r) => !qs || (r.ospite_nome || "").toLowerCase().includes(qs));

  const modeToggle = (
    <div className="flex rounded-lg border overflow-hidden text-xs font-semibold" data-testid={`${testid}-mode`}>
      <button onClick={() => setMode("giorno")} data-testid={`${testid}-mode-giorno`} className={`px-3 py-2 transition-colors ${mode === "giorno" ? "bg-primary text-white" : "hover:bg-secondary"}`}>Giornaliero</button>
      <button onClick={() => setMode("storico")} data-testid={`${testid}-mode-storico`} className={`px-3 py-2 transition-colors ${mode === "storico" ? "bg-primary text-white" : "hover:bg-secondary"}`}>Storico</button>
    </div>
  );

  const sogliaNA = rowsAll[0]?.soglia_non_alim || 2;
  const alimAllertaBox = (
    <AllertaBox tone="bad" title={selectedStructure === "all" ? `Non ha ${verbo} da più giorni di fila` : `Non ha ${verbo} da ${sogliaNA}+ giorni di fila`} icon={AlertTriangle}
      items={alimAllerte} testid={`${testid}-allerta-box`} onItemClick={(it) => navigate(`/ospiti/${it.ospite_id}?tab=${tipo === "beve" ? "non_beve" : "non_mangia"}`)}
      renderItem={(it) => (<span><b>{it.cognome} {it.nome}</b> <span className="ml-1 text-red-700 font-bold">{it.giorni}g di fila</span>{it.soglia ? <span className="ml-1 text-[10px] text-muted-foreground">(soglia {it.soglia})</span> : ""}</span>)} />
  );

  const dayContent = (
    <>
      {alimAllertaBox}
      <RecapGiorno operatori={alimOperatoriGiorno} stats={alimStats} color="#1f7a5c" opLabel="controlli" />
      <UscitiInfoBox usciti={rowsAll.filter(isFuori)} testid={`${testid}-usciti-box`} />
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-semibold ${segnCount > 0 ? "bg-red-50 border-red-200 text-red-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`} data-testid={`${testid}-count`}>
          {segnCount > 0 ? <><AlertTriangle size={15} /> {segnCount} da segnalare</> : <><Check size={15} /> Nessuna segnalazione</>}
        </div>
        <SegnalazioniBadge categoria={categoria} aperte={segAperte} />
        <ElencoCorrezioniBtn categoria={tipo} structureId={selectedStructure} mese={mese} />
        {mustUnlock && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-3 py-1.5 text-sm font-semibold"><AlertTriangle size={15} /> Sblocca con la Firma Rapida</span>}
        <div className="relative ml-auto">
          <Search size={16} className="absolute left-3 top-2.5 text-muted-foreground" />
          <Input placeholder="Cerca ospite..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-56" data-testid={`${testid}-search`} />
        </div>
      </div>
      {isLoading ? (
        <div className="gr-card p-10 text-center text-muted-foreground">Caricamento…</div>
      ) : rows.length === 0 ? (
        <div className="gr-card p-10 text-center text-muted-foreground" data-testid={`${testid}-empty`}>Nessun ospite presente</div>
      ) : (
        <div className="space-y-2">
          {(() => {
          const attivi = rows.filter((r) => !isDimesso(r));
          const dimessi = rows.filter(isDimesso);
          const renderRow = (r, num) => {
            const rec = records[r.ospite_id] || {};
            const segnalato = rec.stato === "segnalato";
            const momenti = rec.momenti || [];
            const dim = isDimesso(r);
            const fuori = isFuori(r);
            const fuoriDal = r.fuori_dal || null;
            const fuoriOggi = r.fuori_giorno ?? (fuori && (!fuoriDal || selDate >= fuoriDal));
            const fuoriDalLbl = fuoriDal ? ` dal ${fuoriDal.slice(8, 10)}/${fuoriDal.slice(5, 7)}` : "";
            return (
              <div key={r.ospite_id} className={`rounded-xl border p-3 transition-colors ${fuoriOggi ? "bg-amber-50/60 border-amber-200" : segnalato ? "border-red-300 bg-red-50" : fuori ? "bg-amber-50/40" : "bg-white"}`} data-testid={`${testid}-row-${r.ospite_id}`}>
                <div className="flex flex-col xl:flex-row xl:items-center gap-2 xl:gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold xl:truncate flex items-center gap-2"><span className="shrink-0 grid place-items-center h-6 w-6 rounded-full bg-secondary text-muted-foreground text-[11px] font-bold tabnum">{num != null ? num : "–"}</span><span className="min-w-0 xl:truncate"><button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/ospiti/${r.ospite_id}?tab=${tipo === "beve" ? "non_beve" : "non_mangia"}`); }} className="hover:underline text-left" data-testid={`${testid}-nome-${r.ospite_id}`}>{r.cognome} {r.nome}</button><EsBadge id={r.ospite_id} /><CorrettoBadge correzioni={corrMap[`${r.ospite_id}_${selDate}`]} testid={`${testid}-corretto-${r.ospite_id}`} />{r.camera && <span className="text-xs text-muted-foreground font-normal"> · Camera/zona {r.camera}</span>}</span>{dim && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 shrink-0">Dimesso</span>}{fuori && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${r.presenza === "ospedale" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid={`${testid}-fuori-badge-${r.ospite_id}`}>{fuoriLabel(r)}{fuoriDalLbl}</span>}</div>
                    {fuoriOggi ? (
                      <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`${testid}-fuori-note-${r.ospite_id}`}>Fuori struttura — non conteggiato nelle statistiche</div>
                    ) : segnalato ? (
                      <div className="mt-1 flex flex-wrap items-center gap-1.5" data-testid={`${testid}-summary-${r.ospite_id}`}>
                        {momenti.map((m) => <span key={m} className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-700" data-testid={`${testid}-summary-momento-${r.ospite_id}-${m}`}>{m}</span>)}
                        {rec.note && <span className="text-[11px] text-red-800 italic" data-testid={`${testid}-summary-note-${r.ospite_id}`}>"{rec.note}"</span>}
                        {rec.updated_by && <span className="text-[11px] text-red-700">· segnalato da {rec.updated_by}</span>}
                      </div>
                    ) : (rec.stato === "ok" && rec.operatore && <div className="text-[11px] text-emerald-700">spuntato da {rec.operatore}</div>)}
                    {(r.giorni_consecutivi > 0 || (r.ultima_segnalazione && !segnalato)) && (
                      <div className="text-[11px] mt-0.5 xl:hidden flex items-center gap-1.5 flex-wrap" data-testid={`${testid}-streak-m-${r.ospite_id}`}>
                        {r.giorni_consecutivi > 0
                          ? <span className={`font-semibold ${r.giorni_consecutivi >= (r.soglia_non_alim || 2) ? "text-red-600" : "text-amber-600"}`}>Non ha {verbo} da {r.giorni_consecutivi} {r.giorni_consecutivi === 1 ? "giorno" : "giorni"}</span>
                          : <span className="text-muted-foreground">Ultima volta non ha {verbo}: <b className="text-foreground capitalize">{fmtRel(r.ultima_segnalazione)}</b></span>}
                      </div>
                    )}
                    {rec.flebo && (
                      <div className="mt-1 xl:hidden" data-testid={`${testid}-flebo-badge-${r.ospite_id}`}>
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold bg-emerald-100 text-emerald-700" title={rec.flebo_note || ""}>
                          <Cross size={11} /> {flebiOptions.find((f) => f.v === rec.flebo)?.l || rec.flebo}{rec.flebo_operatore ? ` · ✍️ ${rec.flebo_operatore}` : ""}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="hidden xl:flex items-start gap-5 shrink-0" data-testid={`${testid}-cols-${r.ospite_id}`}>
                    <div className="w-40">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold leading-tight">Ultima volta<br />non ha {verbo}</div>
                      <div className="text-xs font-semibold capitalize">{r.ultima_segnalazione ? fmtLong(r.ultima_segnalazione) : <span className="text-emerald-600">Mai</span>}</div>
                      {r.ultima_segnalazione && r.ultima_segnalazione_giorni != null && r.ultima_segnalazione_giorni > 0 && <div className="text-[10px] text-muted-foreground">da {r.ultima_segnalazione_giorni} {r.ultima_segnalazione_giorni === 1 ? "giorno" : "giorni"}</div>}
                    </div>
                    <div className="w-28">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Da quanti giorni</div>
                      <div className="text-xs font-bold">{r.giorni_consecutivi > 0 ? <span className={r.giorni_consecutivi >= (r.soglia_non_alim || 2) ? "text-red-600" : "text-amber-600"}>da {r.giorni_consecutivi} {r.giorni_consecutivi === 1 ? "giorno" : "giorni"}</span> : <span className="text-emerald-600">regolare</span>}</div>
                    </div>
                    <div className="w-48" data-testid={`${testid}-flebo-col-${r.ospite_id}`}>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Ultimo aiuto sanitario</div>
                      {r.ultima_flebo ? (
                        <>
                          <div className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1"><Cross size={11} /> {flebiOptions.find((f) => f.v === r.ultima_flebo.tipo)?.l || r.ultima_flebo.tipo}</div>
                          <div className="text-[10px] text-muted-foreground leading-tight">{r.ultima_flebo.giorni === 0 ? "oggi" : r.ultima_flebo.giorni === 1 ? "da 1 giorno" : `da ${r.ultima_flebo.giorni} giorni`} · <span className="capitalize">{fmtRel(r.ultima_flebo.data)}</span>{r.ultima_flebo.operatore ? ` · ✍️ ${r.ultima_flebo.operatore}` : ""}</div>
                        </>
                      ) : <div className="text-xs text-muted-foreground">—</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 w-full xl:w-auto">
                    <button onClick={() => onOk(r)} disabled={fuoriOggi || !segnalato} title={fuoriOggi ? "Ospite fuori struttura" : (!segnalato ? `Predefinito: ha ${verbo}` : "Annulla segnalazione")} className={`h-11 flex-1 xl:flex-none px-4 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${fuoriOggi ? "bg-slate-200 text-slate-400 cursor-not-allowed" : !segnalato ? "bg-green-600 text-white cursor-default" : "bg-secondary hover:bg-green-100"}`} data-testid={`${testid}-ok-${r.ospite_id}`}><Check size={18} /> Ha {verbo}</button>
                    <button onClick={() => onNo(r)} disabled={fuoriOggi} title={fuoriOggi ? "Ospite fuori struttura" : ""} className={`h-11 flex-1 xl:flex-none px-4 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${fuoriOggi ? "bg-slate-200 text-slate-400 cursor-not-allowed" : segnalato ? "bg-red-600 text-white" : "bg-secondary hover:bg-red-100"}`} data-testid={`${testid}-no-${r.ospite_id}`}><X size={18} /> Non ha {verbo}</button>
                    <button onClick={() => openFlebo(r)} disabled={fuoriOggi} title={fuoriOggi ? "Ospite fuori struttura" : "Aiuto sanitario"}
                      className={`h-11 w-14 shrink-0 rounded-lg flex flex-col items-center justify-center leading-none transition-colors ${fuoriOggi ? "bg-slate-200 text-slate-400 cursor-not-allowed" : rec.flebo ? "bg-emerald-600 text-white" : "bg-secondary hover:bg-emerald-100 text-emerald-700"}`}
                      data-testid={`${testid}-flebo-btn-${r.ospite_id}`}>
                      <Cross size={16} fill={rec.flebo ? "#fff" : "none"} />
                      <span className="text-[7px] font-bold mt-0.5">Aiuto Sanitario</span>
                    </button>
                    <SegnalazioneButton categoria={categoria} structureId={selectedStructure} data={selDate}
                      ospiteId={r.ospite_id} ospiteNome={`${r.cognome} ${r.nome}`}
                      entry={segByKey[`${r.ospite_id}_${selDate}`]} onChanged={segRefetch} />
                  </div>
                </div>
                {segnalato && (expanded[r.ospite_id] ? (
                  <div className="mt-3 pt-3 border-t border-red-200" data-testid={`${testid}-detail-${r.ospite_id}`}>
                    <div className="text-xs font-semibold text-red-700 mb-2">{momentiLabel}? (tocca per selezionare)</div>
                    <div className="flex flex-wrap gap-2">
                      {momentiOptions.map((m) => {
                        const sel = momenti.includes(m);
                        return (
                          <button key={m} onClick={() => toggleMomento(r, m)} data-testid={`${testid}-momento-${r.ospite_id}-${m}`}
                            className={`min-h-[44px] px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${sel ? "bg-red-600 text-white border-red-600" : "bg-white hover:bg-red-50 border-red-200 text-red-900"}`}>
                            {m}
                          </button>
                        );
                      })}
                    </div>
                    <Input placeholder="Nota (facoltativa)" value={noteDraft[r.ospite_id] ?? rec.note ?? ""} onChange={(e) => setNoteDraft((p) => ({ ...p, [r.ospite_id]: e.target.value }))} className="mt-2 w-full h-11 text-sm" data-testid={`${testid}-note-${r.ospite_id}`} />
                    <div className="mt-2 flex justify-end">
                      <Button onClick={() => onSalva(r)} className="h-10 bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid={`${testid}-salva-${r.ospite_id}`}><Check size={16} className="mr-1.5" /> Salva</Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 flex justify-end">
                    <button onClick={() => { setExpanded((p) => ({ ...p, [r.ospite_id]: true })); setNoteDraft((p) => ({ ...p, [r.ospite_id]: rec.note || "" })); }} className="text-xs font-semibold text-red-700 hover:underline" data-testid={`${testid}-modifica-${r.ospite_id}`}>Modifica quando / nota</button>
                  </div>
                ))}
              </div>
            );
          };
          return (
            <>
              {attivi.map((r, i) => renderRow(r, i + 1))}
              {dimessi.length > 0 && (
                <div data-testid={`${testid}-dimessi-section`}>
                  <button onClick={() => setShowDimessi((v) => !v)} data-testid={`${testid}-toggle-dimessi`} className="w-full flex items-center gap-2 text-left px-3 py-2.5 rounded-lg bg-orange-50 border border-orange-200 mt-2">
                    <ChevronDown size={16} className={`text-orange-700 transition-transform ${showDimessi ? "" : "-rotate-90"}`} />
                    <span className="font-bold text-sm text-orange-800">Dimessi</span>
                    <span className="text-xs font-semibold text-orange-700 bg-orange-200 rounded-full px-2 py-0.5 tabnum">{dimessi.length}</span>
                  </button>
                  {showDimessi && <div className="space-y-2 mt-2">{dimessi.map((r) => renderRow(r, null))}</div>}
                </div>
              )}
            </>
          );
          })()}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-3">Di default l'ospite è considerato OK. Tocca <b>Non ha {verbo}</b> per segnalare chi non ha {verbo}, indicando quando e una nota. Ogni segnalazione genera una notifica (firma con l'operatore sbloccato via PIN).</p>
    </>
  );

  return (
    <div data-testid={`${testid}-page`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Icon size={22} className="text-primary" /> {title}</h1>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {modeToggle}
          {mode === "giorno" ? (
            <ExportButton filename={testid} title={title} subtitle={fmtLong(selDate)} columns={cols} rows={exportRows} color="#1f7a5c" testid={`${testid}-export`} />
          ) : (
            <ExportButton filename={`storico-${testid}`} title={`Storico ${title}`} subtitle={`${stoDa} → ${stoA}`} columns={stoCols} rows={storico} color="#dc2626" testid={`${testid}-sto-export`} />
          )}
        </div>
      </div>

      {mode === "giorno" && (
        <MeseGiorniEsplosi embedded color="#1f7a5c"
          mese={mese} onMese={setMese} openDay={openDay} onOpenDay={setOpenDay}
          counts={meseData?.counts || {}} countLabel="controlli"
          monthTop={<>{alimAllertaBox}<RecapOperatoriMese operatori={meseData?.operatori || []} color="#1f7a5c" label="controlli" icon={Icon} /></>}>
          {dayContent}
        </MeseGiorniEsplosi>
      )}

      {mode === "storico" && (
        <>
          <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid={`${testid}-storico-controls`}>
            <span className="text-xs font-semibold text-muted-foreground">Dal</span>
            <Input type="date" value={stoDa} max={stoA} onChange={(e) => setStoDa(e.target.value)} className="w-[150px]" data-testid={`${testid}-sto-da`} />
            <span className="text-xs font-semibold text-muted-foreground">al</span>
            <Input type="date" value={stoA} min={stoDa} onChange={(e) => setStoA(e.target.value)} className="w-[150px]" data-testid={`${testid}-sto-a`} />
            <div className="rounded-lg border px-3 py-1.5 text-sm font-semibold bg-red-50 border-red-200 text-red-700" data-testid={`${testid}-sto-count`}>{storicoF.length} segnalazioni</div>
            <div className="relative ml-auto">
              <Search size={16} className="absolute left-3 top-2.5 text-muted-foreground" />
              <Input placeholder="Cerca ospite..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-56" data-testid={`${testid}-sto-search`} />
            </div>
          </div>
          <div className="gr-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-secondary/60">
                <th className="text-left p-3 font-bold">Data</th>
                <th className="text-left p-3 font-bold">Ospite</th>
                <th className="text-left p-3 font-bold">{momentiLabel}</th>
                <th className="text-left p-3 font-bold">Nota</th>
                <th className="text-left p-3 font-bold">Registrato da</th>
              </tr></thead>
              <tbody>
                {storicoF.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground" data-testid={`${testid}-sto-empty`}>Nessuna segnalazione nel periodo</td></tr>}
                {storicoF.map((r, i) => (
                  <tr key={i} className="border-t" data-testid={`${testid}-sto-row-${i}`}>
                    <td className="p-3 tabnum whitespace-nowrap">{fmtLong(r.data)}</td>
                    <td className="p-3 font-semibold"><button type="button" onClick={() => navigate(`/ospiti/${r.ospite_id}?tab=${tipo === "beve" ? "non_beve" : "non_mangia"}`)} className="hover:underline text-left">{r.ospite_nome}</button><EsBadge id={r.ospite_id} nome={r.ospite_nome} />{r.camera ? <span className="text-xs text-muted-foreground font-normal"> · Cam {r.camera}</span> : ""}</td>
                    <td className="p-3">{(r.momenti || []).length ? <div className="flex flex-wrap gap-1">{r.momenti.map((f, j) => <span key={j} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">{f}</span>)}</div> : <span className="text-xs text-muted-foreground">Non specificato</span>}</td>
                    <td className="p-3 text-muted-foreground">{r.note || "—"}</td>
                    <td className="p-3 text-xs text-muted-foreground">{r.updated_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <ConfirmUndoDialog ctx={confirmUndo} onClose={() => setConfirmUndo(null)} />
      <FleboDialog ctx={flebo} options={flebiOptions} label={flebiLabel} onClose={() => setFlebo(null)} onSave={saveFlebo} />
    </div>
  );
}
